import { createActions } from 'redux-actions';
import { HealthClaim } from '@models/HealthClaim';
import {
  GetHealthClaimParams,
  UpdateHealthClaimParams,
  UploadHealthClaimDocumentsParams,
  UpdateHealthClaimSettlementLetterNotesParams,
  ApproveHealthClaimParams
} from '@apis/HealthClaimApi';
import { BenefitLimit } from '@models/BenefitLimit';
import { GetAllBenefitLimitParams } from '@apis/BenefitLimitApi';
import { PolicyBenefitLimit } from '@models/PolicyBenefitLimit';
import { GetAllPolicyBenefitLimitParams } from '@apis/PolicyBenefitLimitApi';
import {
  GetAllHealthClaimCostItemParams,
  CreateHealthClaimCostItemParams,
  UpdateHealthClaimCostItemParams,
  DeleteHealthClaimCostItemParams,
  ApproveAllHealthClaimCostItemParams
} from '@apis/HealthClaimCostItemApi';
import { HealthClaimCostItem } from '@models/HealthClaimCostItem';
import { DiagnosisCode } from '@models/DiagnosisCode';
import { HealthClaimBillingItem } from '@models/HealthClaimBillingItem';
import {
  GetAllHealthClaimBillingItemParams,
  BulkCreateHealthClaimBillingItemParams,
  UpdateHealthClaimBillingItemParams,
  DeleteHealthClaimBillingItemParams
} from '@apis/HealthClaimBillingItemApi';
import { BillingItemWithPrice } from '@models/BillingItemWithPrice';
import { GetAllBillingItemParams } from '@apis/BillingItemApi';
import { GetAllHealthPolicyBenefitLimitsWithAccumulatedPaidAmountsParams } from '@apis/InsurancePolicyUserApi';

export type State = {
  serverErrors: object;
  healthClaim: HealthClaim | null | undefined;
  isUpdatingHealthClaim: boolean;
  benefitLimits?: BenefitLimit[] | null;
  policyBenefitLimits?: PolicyBenefitLimit[] | null;
  healthClaimCostItems?: HealthClaimCostItem[] | null;
  diagnosisCodeOptions?: DiagnosisCode[];
  isDiagnosisCodeSearching?: boolean;
  healthClaimBillingItems?: HealthClaimBillingItem[] | null;
  billingItems?: BillingItemWithPrice[] | null;
  policyBenefitLimitsWithAccumulatedPaidAmounts?: PolicyBenefitLimit[] | null;
};
export type DiagnosisCodeSearchPayload = { search: string };
export type HealthClaimFetchPayload = GetHealthClaimParams;
export type HealthClaimUpdatePayload = UpdateHealthClaimParams;
export type HealthClaimUploadDocumentsPayload = UploadHealthClaimDocumentsParams;
export type HealthClaimSettlementLetterNotesUpdatePayload = UpdateHealthClaimSettlementLetterNotesParams;
export type HealthClaimApprovePayload = ApproveHealthClaimParams;
export type BenefitLimitsFetchPayload = GetAllBenefitLimitParams;
export type PolicyBenefitLimitsFetchPayload = GetAllPolicyBenefitLimitParams;
export type HealthClaimCostItemsFetchPayload = GetAllHealthClaimCostItemParams;
export type HealthClaimCostItemCreatePayload = CreateHealthClaimCostItemParams;
export type HealthClaimCostItemUpdatePayload = UpdateHealthClaimCostItemParams;
export type HealthClaimCostItemDeletePayload = DeleteHealthClaimCostItemParams;
export type HealthClaimCostItemsApprovePayload = ApproveAllHealthClaimCostItemParams;
export type HealthClaimBillingItemsFetchPayload = GetAllHealthClaimBillingItemParams;
export type HealthClaimBillingItemsBulkCreatePayload = BulkCreateHealthClaimBillingItemParams;
export type HealthClaimBillingItemUpdatePayload = UpdateHealthClaimBillingItemParams;
export type HealthClaimBillingItemDeletePayload = DeleteHealthClaimBillingItemParams;
export type BillingItemsFetchPayload = GetAllBillingItemParams;
export type PolicyBenefitLimitsWithAccumulatedPaidAmountsFetchPayload = GetAllHealthPolicyBenefitLimitsWithAccumulatedPaidAmountsParams;
export type StateUpdatePayload = Partial<State>;

export const {
  healthClaimFetch,
  healthClaimUpdate,
  healthClaimUploadDocuments,
  healthClaimSettlementLetterNotesUpdate,
  healthClaimApprove,
  benefitLimitsFetch,
  policyBenefitLimitsFetch,
  healthClaimCostItemsFetch,
  healthClaimCostItemCreate,
  healthClaimCostItemCreateSuccess,
  healthClaimCostItemUpdate,
  healthClaimCostItemUpdateSuccess,
  healthClaimCostItemDelete,
  healthClaimCostItemDeleteSuccess,
  healthClaimCostItemsApprove,
  diagnosisCodeSearch,
  healthClaimBillingItemsFetch,
  healthClaimBillingItemsBulkCreate,
  healthClaimBillingItemUpdate,
  healthClaimBillingItemDelete,
  billingItemsFetch,
  policyBenefitLimitsWithAccumulatedPaidAmountsFetch,

  stateUpdate,
} = createActions(
  'HEALTH_CLAIM_FETCH',
  'HEALTH_CLAIM_UPDATE',
  'HEALTH_CLAIM_UPLOAD_DOCUMENTS',
  'HEALTH_CLAIM_SETTLEMENT_LETTER_NOTES_UPDATE',
  'HEALTH_CLAIM_APPROVE',
  'BENEFIT_LIMITS_FETCH',
  'POLICY_BENEFIT_LIMITS_FETCH',
  'HEALTH_CLAIM_COST_ITEMS_FETCH',
  'HEALTH_CLAIM_COST_ITEM_CREATE',
  'HEALTH_CLAIM_COST_ITEM_CREATE_SUCCESS',
  'HEALTH_CLAIM_COST_ITEM_UPDATE',
  'HEALTH_CLAIM_COST_ITEM_UPDATE_SUCCESS',
  'HEALTH_CLAIM_COST_ITEM_DELETE',
  'HEALTH_CLAIM_COST_ITEM_DELETE_SUCCESS',
  'HEALTH_CLAIM_COST_ITEMS_APPROVE',
  'DIAGNOSIS_CODE_SEARCH',
  'HEALTH_CLAIM_BILLING_ITEMS_FETCH',
  'HEALTH_CLAIM_BILLING_ITEMS_BULK_CREATE',
  'HEALTH_CLAIM_BILLING_ITEM_UPDATE',
  'HEALTH_CLAIM_BILLING_ITEM_DELETE',
  'BILLING_ITEMS_FETCH',
  'POLICY_BENEFIT_LIMITS_WITH_ACCUMULATED_PAID_AMOUNTS_FETCH',

  'STATE_UPDATE',
  {
    prefix: 'USER_DASHBOARD_CLAIM_DETAILS_HEALTH_CLAIM_DETAILS',
  },
);
